import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Separator } from '@/components/ui/separator';
import useBrains from '@/hooks/useBrains';
import { cn } from '@/lib/utils';
import { NameAvatar } from '@/modules/humanChat/components/Conversation/MessageArea/ConversationActivity/NameAvatar';
import { NoteEvent } from '@/modules/humanChat/components/Conversation/MessageArea/ConversationActivity/NoteEvent';
import { useComments } from '@/modules/humanChat/hooks/useComments';
import { AutoStart } from '@/modules/tryIt/components/AutoStart/AutoStart';
import { channelSwitches } from '@/modules/tryIt/components/Messages/ChannelDivider/ChannelDivider';
import { FailureLabel } from '@/modules/tryIt/components/Messages/MessageBuble/AgentMessage';
import { MessageBubble } from '@/modules/tryIt/components/Messages/MessageBuble/MessageBubble';
import { RENDERED_RESPONSE_TYPES } from '@/modules/tryIt/components/Messages/MessageBuble/ResponseMessages/ResponseNodeLabel';
import MessageNode from '@/modules/tryIt/components/Messages/MessageNode/MessageNode';
import type { Comment } from '@/shared/types/comments';
import { IntegrationType } from '@/shared/types/integration';
import { AmendPayload, MessageReminder, NodeType, UserMessage } from '@/shared/types/tryIt';
import { getChannelKey } from '@/util/channelName';
import { convertMinutesToSeconds, formatDate, formatSecondsToReadable } from '@/util/dateUtils';
import { isKeyEnter } from '@/util/util';

import { ScrollToSelectedTurn } from './ScrollToSelectedTurn';
import { TurnAnswer } from './TurnAnswer';
import { TurnAnswerSkeleton } from './TurnAnswerSkeleton';
import { TurnRowHeader } from './TurnRowHeader';
import { useSessionContent } from '../../hooks/useSessionContent';
import { useTestSend } from '../../hooks/useTestSend';
import { selectTurn } from '../../redux/actions';
import {
  selectBrainId,
  selectIsReplay,
  selectIsSending,
  selectMessages,
  selectSelectedTurnIndex,
  selectSessionStart,
  selectTryItSessionId,
} from '../../redux/selectors';
import { hasTurnError } from '../../shared/turnError';
import { ConversationTurn } from '../../types/conversation';
import { turnToLegacyNode } from '../../utils/utils';

type Props = { hideEntities?: boolean };

type TimelineItem =
  | { kind: 'node'; node: NodeType; nodeIndex: number; sortKey: number }
  | { kind: 'comment'; comment: Comment; sortKey: number };

type TimelineBlock = {
  human: boolean;
  agentId?: string;
  channel?: string;
  inactivitySeconds?: number;
  items: TimelineItem[];
};

const reminderOf = (node: NodeType): MessageReminder | undefined =>
  (node.messages as UserMessage[] | undefined)?.find(
    (message) => message.type === 'user' && !message.text && message.reminder
  )?.reminder;

const inactivitySecondsOf = (node: NodeType): number | undefined => {
  const reminder = reminderOf(node);
  if (!reminder) {
    return undefined;
  }
  return reminder.reminder_seconds ?? convertMinutesToSeconds(reminder.minutes ?? 0);
};

const toBlocks = (
  timeline: TimelineItem[],
  turns: ConversationTurn[],
  switchedTo: (string | null)[]
): TimelineBlock[] => {
  const blocks: TimelineBlock[] = [];
  let human = false;

  for (const item of timeline) {
    let channel: string | undefined;
    let inactivitySeconds: number | undefined;
    if (item.kind === 'node') {
      const turn = turns[item.nodeIndex];
      if (turn?.category.kind === 'agent') {
        human = true;
      } else if ((turn?.responses.length ?? 0) > 0) {
        human = false;
      }
      channel = switchedTo[item.nodeIndex] ?? undefined;
      inactivitySeconds = inactivitySecondsOf(item.node);
    }
    const last = blocks[blocks.length - 1];
    if (last && last.human === human && !channel && inactivitySeconds === undefined) {
      last.items.push(item);
      continue;
    }
    const joined = human && !last?.human && item.kind === 'node';
    const agentId = joined ? item.node.author_id : undefined;
    blocks.push({ human, agentId, channel, inactivitySeconds, items: [item] });
  }

  return blocks;
};

const blockKey = (block: TimelineBlock) => {
  const first = block.items[0];
  return first.kind === 'node' ? `block-${first.nodeIndex}` : `block-${first.comment.comment_id}`;
};

const selectableIndexOf = (block: TimelineBlock, turns: ConversationTurn[]) => {
  const indexes = block.items.filter((item) => item.kind === 'node').map((item) => item.nodeIndex);
  return indexes.find((index) => turns[index]?.category.kind === 'agent') ?? indexes[0];
};

const autoSelectIndexOf = (blocks: TimelineBlock[], turns: ConversationTurn[], target: number) => {
  const block = blocks.find(
    (b) => b.human && b.items.some((item) => item.kind === 'node' && item.nodeIndex === target)
  );
  return (block && selectableIndexOf(block, turns)) ?? target;
};

const EventRow = ({ children }: { children: ReactNode }) => (
  <div className="flex w-full items-center justify-center gap-2 bg-background p-4">{children}</div>
);

const JoinedConversation = ({ agentId, agentName }: { agentId: string; agentName?: string }) => {
  const { t } = useTranslation();

  return (
    <EventRow>
      <div className="flex items-center gap-1">
        <NameAvatar id={agentId} name={agentName} className="text-xs leading-4 font-medium" />
        <span className="text-xs leading-4 font-normal text-foreground-muted">
          {t('conversation.joined_conversation')}
        </span>
      </div>
    </EventRow>
  );
};

const HumanAgentHeader = () => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-4 px-4 pt-2 pb-2">
      <span className="shrink-0 text-xs leading-4 font-medium text-foreground">
        {t('try_it.human_agent')}
      </span>
      <Separator className="flex flex-1 flex-col items-start" />
    </div>
  );
};

const Inactivity = ({ seconds }: { seconds: number }) => (
  <EventRow>
    <span className="text-xs leading-4 font-normal text-foreground-muted [&_strong]:font-normal">
      <Trans
        i18nKey="conversation.after_duration_of_inactivity"
        values={{ 0: formatSecondsToReadable(seconds) }}
      />
    </span>
  </EventRow>
);

const ContinuedOn = ({ channel }: { channel: string }) => {
  const { t } = useTranslation();

  return (
    <EventRow>
      <span className="text-xs leading-4 font-normal text-foreground-muted">
        {t('try_it.channel_switched', { 0: t(getChannelKey(channel as IntegrationType)) })}
      </span>
    </EventRow>
  );
};

const TurnTime = ({ time, align }: { time: string; align: 'start' | 'end' }) => (
  <span
    className={cn(
      'text-xs leading-4 font-normal text-foreground-muted',
      align === 'end' ? 'self-end' : 'self-start'
    )}
  >
    {`${formatDate(time, 'MMM d, yyyy')} · ${formatDate(time, 'hh:mm a')}`}
  </span>
);

// Items missing a timestamp sort to the end (Infinity) — without this, the
// `iso ? ... : 0` fallback would push them ahead of every real entry.
const toTimestamp = (iso?: string) => {
  if (!iso) {
    return Number.POSITIVE_INFINITY;
  }
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
};

/**
 * The modal's message list, driven by the `tryIt` slice through direct
 * selectors. Reuses the response renderers in `modules/tryIt`
 * (`MessageNode`, `MessageBubble`, `ResponseMessages/*`, `AutoStart`) unchanged;
 * never reads from `state.tryIt`. The composer footer is mounted as a sibling
 * inside `<LeftSide>` and is not rendered here.
 */
export const Messages = ({ hideEntities = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const turns = useSelector(selectMessages);
  const isReplay = useSelector(selectIsReplay);
  const isSending = useSelector(selectIsSending);
  const sessionId = useSelector(selectTryItSessionId);
  const sessionStart = useSelector(selectSessionStart);
  const brainId = useSelector(selectBrainId);
  const selectedTurnIndex = useSelector(selectSelectedTurnIndex);

  const { brain } = useBrains(brainId);

  // Replay-only: fetch the session content for `desk_id` (notes interleave).
  // Apollo's normalized cache shares this with the modal-root fetch.
  const { data: content } = useSessionContent(
    isReplay ? sessionId : undefined,
    isReplay ? sessionStart : undefined
  );

  const agentNames = new Map(
    (content?.participated_agents ?? []).map((agent) => [agent.agent_id, agent.agent_name])
  );

  const { comments } = useComments(
    isReplay ? (content?.desk_id ?? '') : '',
    isReplay ? (sessionId ?? '') : ''
  );

  const { send } = useTestSend();

  const bodyRef = useRef<HTMLDivElement>(null);

  const [animationBaseline, setAnimationBaseline] = useState({ sessionId, count: turns.length });
  if (animationBaseline.sessionId !== sessionId) {
    setAnimationBaseline({ sessionId, count: turns.length });
  }

  const resetLayout = useCallback(() => {
    if (!bodyRef.current || isReplay) {
      return;
    }
    // The reused legacy `AmendWrapper` exposes its open state as a DOM attribute
    // (`data-amend-open`) rather than via Redux; querying it is the same bridge
    // the legacy modal uses. Skip auto-scroll while an amend editor is open so
    // we don't yank the user out of their in-progress edit.
    const amendEditorOpen = bodyRef.current.querySelector('[data-amend-open="true"]');
    if (amendEditorOpen) {
      return;
    }
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [isReplay]);

  useEffect(() => {
    const timer = setTimeout(() => resetLayout(), 200);
    return () => clearTimeout(timer);
  }, [resetLayout]);

  // Map ConversationTurn[] → legacy NodeType[] at the single parser boundary.
  const nodes = useMemo<NodeType[]>(() => turns.map(turnToLegacyNode), [turns]);

  const timeline = useMemo<TimelineItem[]>(() => {
    const nodeItems: TimelineItem[] = nodes.map((node, nodeIndex) => ({
      kind: 'node',
      node,
      nodeIndex,
      sortKey: toTimestamp(node.time),
    }));
    if (!isReplay || !comments?.length) {
      return nodeItems;
    }
    const commentItems: TimelineItem[] = comments.map((comment) => ({
      kind: 'comment',
      comment,
      sortKey: toTimestamp(comment.created),
    }));
    return [...nodeItems, ...commentItems].sort((a, b) => a.sortKey - b.sortKey);
  }, [nodes, comments, isReplay]);

  // Memoised because the auto-select effect reads it, so its identity decides when that runs.
  const blocks = useMemo(
    () => toBlocks(timeline, turns, channelSwitches(nodes)),
    [timeline, turns, nodes]
  );

  // Auto-select a turn when the timeline *grows* (test mode highlights
  // each new message) or when a new session's content loads (first open + replay
  // arrow-key/table navigation). Folding the session-change reset into this one
  // effect — baseline 0 when `sessionId` changed, else the prior length — avoids
  // the effect-ordering race the previous two-effect version had, so navigating
  // to a session with fewer turns still re-selects. Keying on
  // growth (not identity) means a replay translation, which replaces the turns
  // in place at the same length, won't steal a manual selection.
  const autoSelectRef = useRef<{ sessionId?: string; len: number }>({ len: 0 });
  useEffect(() => {
    const prev = autoSelectRef.current;
    const baseline = sessionId === prev.sessionId ? prev.len : 0;
    if (nodes.length > baseline) {
      dispatch(selectTurn(autoSelectIndexOf(blocks, turns, isReplay ? 0 : nodes.length - 1)));
      if (!isReplay && bodyRef.current) {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }
    }
    autoSelectRef.current = { sessionId, len: nodes.length };
  }, [nodes, sessionId, isReplay, dispatch, blocks, turns]);

  const handleOptionClick = useCallback(
    (message: string) => {
      // Skip while sending — a double-click would push two `'pending'` turns.
      if (isReplay || isSending) {
        return;
      }
      void send(message);
    },
    [isReplay, isSending, send]
  );

  const handleTurnClick = useCallback(
    (index: number) => {
      dispatch(selectTurn(index));
    },
    [dispatch]
  );

  const isEmpty = nodes.length === 0;

  // A session no AI agent handled resolves no `brainId`, and still has to render.
  const renderBrainId = brainId ?? '';

  const renderNode = (node: NodeType, index: number, showTyping = false) => {
    // Show amend only when we know the request reached the LLM.
    const showAmend =
      node.collection?.request_code === 6 &&
      (node.collection.response_code === 0 || node.collection.response_code === 1);

    const amendPayload: AmendPayload =
      node.collection?.collection_id && showAmend
        ? {
            request_id: node.request_id || node.nodes_stack?.[0]?.request_id,
            task: 'synthesis',
            session_id: sessionId,
            language: brain?.language,
          }
        : null;
    const correction = node.corrections?.find((c) => c.task === 'synthesis');
    const isActive = selectedTurnIndex === index;
    const animateIn = index >= animationBaseline.count;
    const turn = turns[index];
    const turnFailed = hasTurnError(turn);

    return (
      <MessageNode
        key={node.id ?? JSON.stringify(node)}
        active={isActive}
        handleNodeClick={() => handleTurnClick(index)}
        headerSlot={
          <TurnRowHeader
            turn={turn}
            index={index}
            node={node}
            brainId={brainId}
            isActive={isActive}
          />
        }
        index={index}
        resetLayout={resetLayout}
        reaction={node.reaction}
        request_id={node.request_id}
      >
        {node.messages.map((message) => {
          const key = JSON.stringify(message);
          const bubble = (
            <MessageBubble
              message={message}
              onOptionClick={handleOptionClick}
              onPostback={handleOptionClick}
              collection={node.collection}
              nodes_stack={node.nodes_stack}
              brainId={renderBrainId}
              author_id={node.author_id}
              author_type={node.author_type}
              amendPayload={amendPayload}
              correction={correction}
              hideEntities={hideEntities}
              flat
              turnRow
            />
          );
          if (message.type === 'user') {
            return <React.Fragment key={key}>{bubble}</React.Fragment>;
          }
          // Nothing reached the user, so the error card stands in for the answer.
          if (turnFailed) {
            return null;
          }
          const responses = 'responses' in message ? (message.responses ?? []) : [];
          const shown = responses.filter((r) => RENDERED_RESPONSE_TYPES.has(r.type));
          return (
            <TurnAnswer
              key={key}
              messageCount={shown.length}
              isSelected={isActive}
              animateIn={animateIn}
            >
              {bubble}
            </TurnAnswer>
          );
        })}
        {turnFailed && !isReplay && (
          <TurnAnswer messageCount={0} isSelected={isActive} hasError animateIn={animateIn} />
        )}
        {showTyping && (
          <TurnAnswer messageCount={0} isSelected={isActive} isLoading animateIn={animateIn}>
            <TurnAnswerSkeleton />
          </TurnAnswer>
        )}
      </MessageNode>
    );
  };

  const renderHumanNode = (node: NodeType, index: number) => {
    const turn = turns[index];
    const isAgent = turn.category.kind === 'agent';
    const failureCodes = [
      ...new Set(
        node.messages.flatMap((message) => {
          const { body } = message as { body?: { error_code?: string } };
          return body?.error_code ? [body.error_code] : [];
        })
      ),
    ];

    return (
      <div
        key={node.id ?? JSON.stringify(node)}
        id={node.request_id}
        className={cn(
          'flex min-w-0 flex-col self-stretch px-4 pt-2 pb-4',
          isAgent ? 'items-start gap-1.5 pr-6' : 'items-end gap-2'
        )}
      >
        {node.messages.map((message, messageIndex) => (
          <MessageBubble
            key={JSON.stringify(message)}
            message={message}
            onOptionClick={handleOptionClick}
            onPostback={handleOptionClick}
            collection={node.collection}
            nodes_stack={node.nodes_stack}
            brainId={renderBrainId}
            author_id={node.author_id}
            author_name={node.author_id ? agentNames.get(node.author_id) : undefined}
            author_type={node.author_type}
            amendPayload={null}
            hideEntities={hideEntities}
            flat
            turnRow
            hideAuthor={messageIndex > 0}
          />
        ))}
        {node.time && <TurnTime time={node.time} align={isAgent ? 'start' : 'end'} />}
        {failureCodes.map((errorCode) => (
          <FailureLabel
            key={errorCode}
            errorCode={errorCode}
            className={isAgent ? 'self-start' : 'self-end'}
          />
        ))}
      </div>
    );
  };

  if (isEmpty && !isReplay && brainId) {
    return (
      <div className="flex h-full w-full flex-1 flex-col items-center gap-4 self-stretch py-10">
        <AutoStart brainId={brainId} onOptionClick={handleOptionClick} />
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden">
      <ScrollToSelectedTurn
        containerRef={bodyRef}
        selectedTurnIndex={selectedTurnIndex}
        turnCount={nodes.length}
        isHumanAgent={
          selectedTurnIndex !== undefined && turns[selectedTurnIndex]?.category.kind === 'agent'
        }
      />
      {/* The turn row brings its own padding and runs edge to edge in both modes. */}
      <div
        className="flex min-h-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-auto"
        ref={bodyRef}
      >
        <div className="flex flex-col">
          {blocks.map((block) => {
            const body = block.items.map((item) => {
              if (item.kind === 'comment') {
                return (
                  <div key={item.comment.comment_id} className="px-4 py-2">
                    <NoteEvent comment={item.comment} readOnly />
                  </div>
                );
              }
              // Typing indicator renders inside the last (pending) node, like the legacy modal.
              const showTyping = isSending && !isReplay && item.nodeIndex === nodes.length - 1;
              return (
                <React.Fragment key={item.node.id ?? JSON.stringify(item.node)}>
                  {block.human
                    ? renderHumanNode(item.node, item.nodeIndex)
                    : renderNode(item.node, item.nodeIndex, showTyping)}
                </React.Fragment>
              );
            });

            const separator = (
              <>
                {block.inactivitySeconds !== undefined && (
                  <Inactivity seconds={block.inactivitySeconds} />
                )}
                {block.channel && <ContinuedOn channel={block.channel} />}
                {block.agentId && (
                  <JoinedConversation
                    agentId={block.agentId}
                    agentName={agentNames.get(block.agentId)}
                  />
                )}
              </>
            );

            if (!block.human) {
              return (
                <React.Fragment key={blockKey(block)}>
                  {separator}
                  {body}
                </React.Fragment>
              );
            }

            const blockIndexes = block.items
              .filter((item) => item.kind === 'node')
              .map((item) => item.nodeIndex);
            const isActive = blockIndexes.includes(selectedTurnIndex ?? -1);
            const selectIndex = selectableIndexOf(block, turns);

            return (
              <React.Fragment key={blockKey(block)}>
                {separator}
                <div
                  className={cn(
                    'group flex cursor-default flex-col border-l-[3px] border-l-transparent py-2 transition',
                    isActive
                      ? 'border-l-informational bg-bg-info-soft [&_.turn-card]:bg-background'
                      : 'hover:bg-highlight-background'
                  )}
                  onClick={() => selectIndex !== undefined && handleTurnClick(selectIndex)}
                  onKeyDown={(e) => {
                    if (isKeyEnter(e) && selectIndex !== undefined) {
                      handleTurnClick(selectIndex);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  data-turn-row={selectIndex}
                >
                  <HumanAgentHeader />
                  {body}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
